import axios from 'axios';
import { VehicleAttributeValueEntity } from 'src/entities/VehicleAttributeValue.entity';

/** 突き合わせは前後の空白と大小文字を無視する（API の normalize_for_match と同じ規則） */
export const normalizeForMatch = (value: string): string => value.trim().toLowerCase();

export type VehicleAttributeDraft = {
  name: string;
  aliases: string[];
};

/**
 * 保存時に送るエイリアス。改名なら、新しい表示名になった値をエイリアスから外す。
 *
 * 改名すると旧表示名がエイリアスへ自動で足される（API の retain_renamed_alias）ので、
 * 元の名前へ戻すときは必ず「表示名と同じエイリアス」ができる。API 側は改名のときだけ
 * これを外して通すので、FE も同じ規則にしないと画面から戻せない。
 *
 * @param currentName 保存前の表示名。追加のときは undefined
 */
export const aliasesForSave = (
  draft: VehicleAttributeDraft,
  currentName?: string,
): string[] => {
  const name = draft.name.trim();
  if (currentName === undefined || normalizeForMatch(currentName) === normalizeForMatch(name)) return draft.aliases;

  return draft.aliases.filter((alias) => normalizeForMatch(alias) !== normalizeForMatch(name));
};

/**
 * ダイアログの入力を保存前に検査する。
 *
 * values はそのカテゴリの全件を渡し、編集中の値は targetId で除く（追加なら null）。
 * Why not 呼び出し側で除外した配列を渡す: 除外を忘れると「自分の名前のまま状態だけ変える」
 * 操作が自分自身との重複で弾かれる。
 *
 * @param currentName 保存前の表示名。渡すと改名かどうかを見て、改名で表示名になる
 *   エイリアスは弾かない（aliasesForSave が外すため）
 * @returns エラー文言。問題なければ null
 */
export const validateDraft = (
  draft: VehicleAttributeDraft,
  values: VehicleAttributeValueEntity[],
  targetId: number | null,
  currentName?: string,
): string | null => {
  const name = draft.name.trim();
  if (!name) return '表示名は必須です。';

  const aliases = aliasesForSave(draft, currentName);
  const selfAlias = aliases.find((alias) => normalizeForMatch(alias) === normalizeForMatch(name));
  if (selfAlias) return `エイリアス「${selfAlias}」は表示名と同じです。`;

  const others = values.filter((it) => it.id !== targetId);
  if (others.some((it) => normalizeForMatch(it.name) === normalizeForMatch(name))) {
    return `表示名「${name}」は同じカテゴリ内で既に使用されています。`;
  }
  if (others.some((it) => it.aliases.some((alias) => normalizeForMatch(alias) === normalizeForMatch(name)))) {
    return `表示名「${name}」は他の値のエイリアスと重複しています。`;
  }

  return aliases.reduce<string | null>((found, alias) => {
    if (found) return found;
    if (others.some((it) => normalizeForMatch(it.name) === normalizeForMatch(alias))) {
      return `エイリアス「${alias}」は他の値の表示名と重複しています。`;
    }
    const conflict = others.find((it) => it.aliases.some((it2) => normalizeForMatch(it2) === normalizeForMatch(alias)));
    return conflict ? `エイリアス「${alias}」は「${conflict.name}」で既に使用されています。` : null;
  }, null);
};

/**
 * ドラッグの結果の並び順を作る。
 *
 * ids はそのカテゴリの全件を渡す。PATCH /reorder は id が全件揃っていないと 400 を返すので、
 * 状態フィルタで隠れている行を落とした配列を渡してはいけない。
 */
export const movedIds = (ids: number[], sourceId: number, targetId: number): number[] => {
  const from = ids.indexOf(sourceId);
  const to = ids.indexOf(targetId);
  if (from < 0 || to < 0 || from === to) return ids;

  const reordered = ids.filter((id) => id !== sourceId);
  reordered.splice(to, 0, sourceId);
  return reordered;
};

/**
 * 絞り込み中の並べ替え。見えている行だけを入れ替え、隠れている行は元の位置に留める。
 *
 * Why not 全件の配列でそのまま movedIds する: 挿入先が全件配列の添字になるので、
 * 利用者が見ていない行が巻き添えで前後する。「有効」だけを絞って並べ替えたつもりが
 * 無効な値の位置まで変わり、あとで有効化したときに思わぬ場所に出る。
 *
 * 絞り込んでいないときは visibleIds === allIds なので movedIds と同じ結果になる。
 *
 * @returns 並びが変わらないときは受け取った allIds をそのまま返す
 */
export const movedIdsWithinVisible = (
  allIds: number[],
  visibleIds: number[],
  sourceId: number,
  targetId: number,
): number[] => {
  const movedVisible = movedIds(visibleIds, sourceId, targetId);
  if (movedVisible === visibleIds) return allIds;

  const visible = new Set(visibleIds);
  const result = [...allIds];
  let next = 0;
  allIds.forEach((id, index) => {
    if (!visible.has(id)) return;
    result[index] = movedVisible[next];
    next += 1;
  });
  return result;
};

/**
 * 反映中の行への編集・並べ替えを断られたか（#329 P6）。
 *
 * 409 が返るのは画面が「反映中」を知らないままボタンを出していたときだけなので、
 * 呼び出し側は文言を出すのに加えて一覧を取り直す。
 */
export const isConflictError = (error: unknown): boolean => (
  axios.isAxiosError(error) && error.response?.status === 409
);

/**
 * API の 400・409 から画面に出す文言を取り出す。
 *
 * Api::V2::ApplicationController は errors.full_messages を素の配列で返すので、
 * TruckShifts の extractApiErrorMessages（{ errors: [...] } 前提）では拾えない。
 */
export const apiErrorMessages = (error: unknown): string[] => {
  if (!axios.isAxiosError(error)) return [];

  const data: unknown = error.response?.data;
  if (Array.isArray(data)) return data.filter((it): it is string => typeof it === 'string');
  if (typeof data === 'string') {
    const trimmed = data.trim();
    return trimmed === '' || trimmed.startsWith('<') ? [] : [trimmed];
  }
  if (data && typeof data === 'object') {
    const candidate: unknown = (data as { errors?: unknown }).errors;
    if (Array.isArray(candidate)) return candidate.filter((it): it is string => typeof it === 'string');
    if (typeof candidate === 'string') return [candidate];
  }
  return [];
};
